import { DropOverlay, type DropOverlayProps } from '@flashka/shared/ui';
import {
  type DropGroups,
  type DropKind,
  type DroppedFileInfo,
  type DropSkipReason,
  type DropSplit,
  groupDrop,
  groupDropSkips,
  IN_APP_IMAGE_DRAG_MIME,
  IN_APP_IMAGE_FILEPATH_MIME,
  isInAppImageDrag,
  shortFileName,
  splitDrop,
} from '@flashka/shared/upload/drop-kind';

import { type TFunction } from 'i18next';
import {
  type CSSProperties,
  type DragEvent as ReactDragEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/lib/flashka-toast';
import { playSound } from '@/lib/sound';
import { cn } from '@/lib/utils';

/**
 * Drag & drop of files from the OS onto Flashka — and of in-app images (a
 * note image, the image-occlusion selection) onto the targets that opt in.
 *
 * Three pieces:
 *  - `installFileDropGuard()` — a file dropped where nothing takes it must
 *    never make the browser open the file and leave the app (the user's
 *    state is lost). It runs in the BUBBLE phase and only for drops nobody
 *    claimed: ProseMirror ignores events whose default is already prevented,
 *    so a capture-phase guard would kill image drops into notes.
 *  - the internal-drag flag — "this drag started in this document". An
 *    in-page `<img>` drag carries `Files` in Chrome; it must not arm a
 *    create-a-deck target.
 *  - `useFileDrop` / `<FileDropArea>` — a surface's drop target. Claims a drop
 *    by `preventDefault` + `stopPropagation` ("claim-and-stop"), and skips a
 *    drop an inner zone already claimed (`defaultPrevented`).
 */

let dragStartedHere = false;
/** Every armed area's reset, so a window-level drop/dragend clears them all. */
const armedResets = new Set<() => void>();

const hasFiles = (dt: DataTransfer | null | undefined) =>
  !!dt && Array.from(dt.types).includes('Files');

/**
 * An in-app image drag dropped OUTSIDE the browser (the desktop, a Finder
 * window) lands as a real image file. With only the in-app type on the drag,
 * Finder wrote an empty file named "data". `DownloadURL` is Chromium's
 * (Chrome, Edge, Arc); Safari and Firefox ignore it.
 */
export function offerImageAsFile(dt: DataTransfer, src: string): void {
  // A crop is a `data:image/png` URL; a note image a stored URL ending in its type.
  const ext = (
    /^data:image\/(\w+)/.exec(src)?.[1] ??
    /\.(png|jpe?g|gif|webp)(?:[?#]|$)/i.exec(src)?.[1] ??
    'png'
  ).toLowerCase();
  const mime = `image/${ext === 'jpg' ? 'jpeg' : ext}`;
  dt.setData('DownloadURL', `${mime}:flashka-image.${ext}:${src}`);
}

/**
 * The occlusion crop being dragged. It travels HERE, not on the DataTransfer:
 * any data a page writes there stops Safari handing the dragged `<img>` to
 * Finder as an image file (Finder wrote an empty "data" file instead).
 */
let draggedImageSrc: string | null = null;

export function startInAppImageDrag(src: string): void {
  draggedImageSrc = src;
}

/** An in-app image drag: a note image (on the DataTransfer) or the crop. */
export function carriesInAppImage(dt: DataTransfer): boolean {
  return isInAppImageDrag(dt.types) || !!draggedImageSrc;
}

/** The dragged in-app image's src — read it in `drop`, not `dragover`. */
export function inAppImageSrc(dt: DataTransfer): string {
  return isInAppImageDrag(dt.types)
    ? dt.getData(IN_APP_IMAGE_DRAG_MIME)
    : (draggedImageSrc ?? '');
}

/** A drag of OS files — not an in-page image, a note image or a quiz tile. */
export function isExternalFileDrag(dt: DataTransfer | null | undefined): boolean {
  return !dragStartedHere && hasFiles(dt) && !isInAppImageDrag(dt?.types);
}

const isFileInput = (target: EventTarget | null) =>
  target instanceof HTMLInputElement && target.type === 'file';

let guardInstalled = false;

export function installFileDropGuard(): void {
  if (guardInstalled || typeof window === 'undefined') return;
  guardInstalled = true;

  window.addEventListener('dragover', (e) => {
    if (!hasFiles(e.dataTransfer) || e.defaultPrevented || isFileInput(e.target)) return;
    e.preventDefault();
    // "Not allowed" cursor: nothing here takes the file. Safe only because
    // claimed drags arrive already prevented (skipped above).
    e.dataTransfer!.dropEffect = 'none';
  });
  window.addEventListener('drop', (e) => {
    if (hasFiles(e.dataTransfer) && !e.defaultPrevented && !isFileInput(e.target)) {
      e.preventDefault();
    }
    dragStartedHere = false;
  });

  document.addEventListener('dragstart', () => (dragStartedHere = true), true);
  // `dragend` never fires when the source unmounted mid-drag; no pointer
  // events fire DURING a drag, so the next pointermove means it is over.
  const clearFlag = () => {
    dragStartedHere = false;
    draggedImageSrc = null;
  };
  document.addEventListener('dragend', clearFlag, true);
  window.addEventListener('pointermove', clearFlag, true);

  // A drop or a cancelled drag anywhere disarms every area (Esc, leaving the
  // window — cases where no dragleave reaches the area).
  const resetAll = () => armedResets.forEach((reset) => reset());
  window.addEventListener('drop', resetAll, true);
  document.addEventListener('dragend', resetAll, true);
}

/** Test seam: the flag lives in module state. */
export function resetFileDropStateForTests(): void {
  dragStartedHere = false;
  draggedImageSrc = null;
  armedResets.clear();
}

type DropTargetElement = HTMLElement;

export type FileDropOptions = {
  enabled?: boolean;
  /** File kinds this surface takes; a drop of nothing else is left unclaimed. */
  accept: readonly DropKind[];
  onDrop?: (split: DropSplit<File>) => void;
  /**
   * Takes EVERY accepted kind of a drop instead of `onDrop`'s one — an
   * existing deck's targets (`groupDrop`).
   */
  onDropAll?: (drop: DropGroups<File>) => void;
  /** Opt in to in-app image drags (note image, occlusion selection). */
  onInAppImage?: (file: File) => void;
  /** Replaces the generic "some files weren't added" toast. */
  onSkipped?: () => void;
};

const WATCHDOG_MS = 1000;

const SKIP_REASON_KEY = {
  folder: 'createMenu.dropSkipped.reason.folder',
  video: 'createMenu.dropSkipped.reason.video',
  unsupported: 'createMenu.dropSkipped.reason.unsupported',
  notHere: 'createMenu.dropSkipped.reason.notHere',
  alone: 'createMenu.dropSkipped.reason.alone',
} as const satisfies Record<DropSkipReason, string>;

/** Names per reason; a long tail is counted, not listed. */
const NAMES_SHOWN = 3;

/**
 * ONE toast for what a drop left out, naming the files and why: one file is
 * the title ("“clip.mp4” wasn't added" · "Video files aren't supported."),
 * several are a sentence per reason.
 */
function toastSkipped(
  t: TFunction,
  files: readonly DroppedFileInfo[],
  accept: readonly DropKind[],
): void {
  toastFilesNotAdded(
    t,
    groupDropSkips(files, accept).map(({ reason, names }) => ({
      names,
      reason: t(SKIP_REASON_KEY[reason]),
    })),
  );
}

/** That toast for files left out later than the drop — `reason` pre-translated. */
export function toastFilesNotAdded(
  t: TFunction,
  groups: readonly { names: readonly string[]; reason: string }[],
): void {
  const only =
    groups.length === 1 && groups[0]!.names.length === 1 ? groups[0] : undefined;
  if (only) {
    toast.error(
      t('createMenu.dropSkipped.one', { name: shortFileName(only.names[0]!) }),
      { description: only.reason },
    );
    return;
  }
  const lines = groups.map(({ reason, names }) => {
    const shown = names.slice(0, NAMES_SHOWN).map((name) => shortFileName(name));
    const rest = names.length - shown.length;
    return t('createMenu.dropSkipped.line', {
      names: shown.join(', ') + (rest > 0 ? ` +${rest}` : ''),
      reason,
    });
  });
  toast.error(t('createMenu.dropSkipped.title'), { description: lines.join(' ') });
}

export function useFileDrop({
  enabled = true,
  accept,
  onDrop,
  onDropAll,
  onInAppImage,
  onSkipped,
}: FileDropOptions) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);
  const entered = useRef(new Set<EventTarget>());
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef({ accept, onDrop, onDropAll, onInAppImage, onSkipped });
  latest.current = { accept, onDrop, onDropAll, onInAppImage, onSkipped };

  const reset = useCallback(() => {
    entered.current.clear();
    if (watchdog.current) clearTimeout(watchdog.current);
    watchdog.current = null;
    setArmed(false);
  }, []);

  useEffect(() => {
    armedResets.add(reset);
    return () => {
      armedResets.delete(reset);
      if (watchdog.current) clearTimeout(watchdog.current);
    };
  }, [reset]);

  useEffect(() => {
    if (!enabled) reset();
  }, [enabled, reset]);

  const accepts = (dt: DataTransfer) =>
    isExternalFileDrag(dt) || (!!latest.current.onInAppImage && carriesInAppImage(dt));

  /** Whether a drag over `e.target` arms this area. */
  const canArm = (e: ReactDragEvent<DropTargetElement>) =>
    enabled &&
    accepts(e.dataTransfer) &&
    // React bubbles portal events through the component tree — a dialog
    // portaled out of this area is not part of it.
    e.currentTarget.contains(e.target as Node) &&
    // Over an editable field the field takes the drop (notes insert images).
    !(e.target as Element).closest?.('[contenteditable="true"]');

  const onDragEnter = (e: ReactDragEvent<DropTargetElement>) => {
    if (!canArm(e)) return;
    entered.current.add(e.target);
    setArmed(true);
  };

  const onDragLeave = (e: ReactDragEvent<DropTargetElement>) => {
    if (!entered.current.size) return;
    entered.current.delete(e.target);
    // Safari reports no relatedTarget; a removed node never sends a leave.
    for (const node of entered.current) {
      if (node instanceof Node && !node.isConnected) entered.current.delete(node);
    }
    if (!entered.current.size) setArmed(false);
  };

  const onDragOver = (e: ReactDragEvent<DropTargetElement>) => {
    if (!entered.current.size) {
      // No dragenter armed it: the watchdog or `enabled` reset it mid-drag.
      if (!canArm(e)) return;
      entered.current.add(e.target);
      setArmed(true);
    }
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    // No setState here — dragover fires every few ms while the pointer rests.
    if (watchdog.current) clearTimeout(watchdog.current);
    watchdog.current = setTimeout(reset, WATCHDOG_MS);
  };

  const handleDrop = (e: ReactDragEvent<DropTargetElement>) => {
    const dt = e.dataTransfer;
    // Read everything synchronously: the DataTransfer is emptied after the event.
    const files = Array.from(dt.files);
    const items = Array.from(dt.items ?? []).filter((item) => item.kind === 'file');
    const inAppSrc = inAppImageSrc(dt);
    const inAppPath = inAppSrc ? dt.getData(IN_APP_IMAGE_FILEPATH_MIME) : '';
    reset();
    if (!enabled || e.defaultPrevented || !e.currentTarget.contains(e.target as Node))
      return;

    const {
      accept: kinds,
      onDrop: handle,
      onDropAll: handleAll,
      onInAppImage: handleInApp,
      onSkipped: handleSkipped,
    } = latest.current;
    if (inAppSrc && handleInApp) {
      e.preventDefault();
      e.stopPropagation();
      // Lazy: the resolver pulls in the API client, which this module (in
      // the app's entry, for the guard) should not.
      import('@/components/editor/lib/note-image-utils')
        .then(({ fetchNoteImageAsFile }) =>
          fetchNoteImageAsFile(inAppPath || null, inAppSrc),
        )
        .then((file) => {
          handleInApp(file);
          playSound('dnd.drop');
        })
        .catch(() => toast.error(t('chat.imageUploadFailed')));
      return;
    }
    if (!isExternalFileDrag(dt)) return;

    const dropped = files.map((file, i) => ({
      file,
      name: file.name,
      type: file.type,
      isDirectory: items[i]?.webkitGetAsEntry?.()?.isDirectory ?? false,
      size: file.size,
    }));
    // `Files` on the drag but none in it: nothing to name in a toast.
    if (!dropped.length) {
      playSound('dnd.invalid');
      return;
    }
    const split = handleAll ? groupDrop(dropped, kinds) : splitDrop(dropped, kinds);
    if (!split || split.skipped.length) {
      if (handleSkipped) handleSkipped();
      else toastSkipped(t, split ? split.skipped : dropped, kinds);
    }
    // Nothing taken: leave the drop unclaimed — the window guard keeps the
    // browser from opening the file.
    if (!split) {
      playSound('dnd.invalid');
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    playSound('dnd.drop');
    const skipped = split.skipped.map((d) => d.file);
    if ('groups' in split)
      handleAll?.({
        groups: split.groups.map((g) => ({
          kind: g.kind,
          files: g.files.map((d) => d.file),
        })),
        skipped,
      });
    else
      handle?.({
        kind: split.kind,
        files: split.files.map((d) => d.file),
        joins: {
          photos: split.joins.photos.map((d) => d.file),
          recordings: split.joins.recordings.map((d) => d.file),
        },
        skipped,
      });
  };

  return {
    armed,
    dropProps: { onDragEnter, onDragLeave, onDragOver, onDrop: handleDrop },
  };
}

export type FileDropAreaProps = FileDropOptions & {
  className?: string;
  style?: CSSProperties;
  /** The armed overlay's copy and the host's radius / z-index. */
  overlay: DropOverlayProps;
  /**
   * Wraps the overlay — e.g. a sticky box so the overlay covers the viewport
   * of a long scrolling page rather than its full height.
   */
  overlayContainerClassName?: string;
  children: ReactNode;
  'data-testid'?: string;
};

/** A surface's root `<div>`, armed while an acceptable drag is over it. */
export function FileDropArea({
  className,
  style,
  overlay,
  overlayContainerClassName,
  children,
  'data-testid': testId,
  ...options
}: FileDropAreaProps) {
  const { armed, dropProps } = useFileDrop(options);
  const overlayNode = armed ? <DropOverlay {...overlay} /> : null;
  return (
    <div
      className={cn('relative', className)}
      style={style}
      data-testid={testId}
      {...dropProps}>
      {children}
      {overlayNode && overlayContainerClassName ? (
        <div aria-hidden className={cn('pointer-events-none', overlayContainerClassName)}>
          {overlayNode}
        </div>
      ) : (
        overlayNode
      )}
    </div>
  );
}
